import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataProvider } from '@work4all/data';

import { FullUser } from '@work4all/models/lib/Classes/FullUser.entity';
import { LicenseInfo } from '@work4all/models/lib/Classes/LicenseInfo.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { LicenseAssignment } from '@work4all/models/lib/Enums/LicenseAssignment.enum';
import { UserClass } from '@work4all/models/lib/Enums/UserClass.enum';

import { Divider } from '../../../../dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '../../../../input/checkbox-radio-item';
import { Collapse } from '../../../collapse';
import { Tooltip } from '../../../tooltip/Tooltip';

import { LocalUserAssignedLicense } from './types';

export type UserLicenseUser = FullUser & {
  isLightUser?: boolean;
  fulllUsersInUse?: number;
  wasFullUser?: boolean;
};

export interface UserLicenseFieldProps {
  value: UserLicenseUser;
  onChange: (lic: LocalUserAssignedLicense) => void;
  onUserChange: (user: UserLicenseUser) => void;
  readonly?: boolean;
  error?: string;
}

const FULL_USER_VISIBLE_MODULES: LicenseAssignment[] = [
  LicenseAssignment.KOMMT_GEHT,
  LicenseAssignment.EXCHANGE_CONNECTOR,
  LicenseAssignment.TICKET_SYSTEM,
];

const FULL_USER_VISIBLE_SPECIAL_MODULES: LicenseAssignment[] = [
  LicenseAssignment.LAGERSCANNER,
];

interface LicenseAssignmentGroup {
  parent: LicenseAssignment;
  children: LicenseAssignment[];
}

const LICENSE_GROUPS: LicenseAssignmentGroup[] = [
  {
    parent: LicenseAssignment.CRM_LIGHT,
    children: [
      LicenseAssignment.ERP_LIGHT,
      LicenseAssignment.PROJEKT_LIGHT,
      LicenseAssignment.TICKETS_LIGHT,
    ],
  },
];

const NON_FULL_USER_VISIBLE_MODULES: LicenseAssignment[] = [
  LicenseAssignment.CRM_LIGHT,
  LicenseAssignment.ERP_LIGHT,
  LicenseAssignment.PROJEKT_LIGHT,
  LicenseAssignment.TICKETS_LIGHT,
  LicenseAssignment.ARBEITSZEITERFASSUNG_LIGHT,
  LicenseAssignment.PROJEKTZEITERFASSUNG_LIGHT,
  LicenseAssignment.REISEKOSTENABRECHNUNG_LIGHT,
  LicenseAssignment.EXCHANGE_CONNECTOR,
];

const NON_FULL_USER_VISIBLE_SPECIAL_MODULES: LicenseAssignment[] = [
  LicenseAssignment.LAGERSCANNER,
];

export const UserLicenseField = (props: UserLicenseFieldProps) => {
  const { value, onChange, onUserChange, readonly, error } = props;
  const { t } = useTranslation();

  const requestData = useMemo<DataRequest>(() => {
    const data: LicenseInfo = {
      licenseCustomerNumber: null,
      modules: [
        {
          assignedToCurrentUserContext: null,
          isLegacyModule: null,
          moduleCount: null,
          moduleCountInUse: null,
          moduleIndex: null,
          module: null,
          userModulAssignIndex: null,
          isLightLicense: null,
          userLicense: null,
        },
      ],
    };
    return { entity: Entities.licenseInfo, data, completeDataResponse: true };
  }, []);
  const { data, refetch } = useDataProvider<LicenseInfo>(requestData);

  const licenseChanged = value.assignedLicenses?.filter(
    (x) => x.assigned
  ).length;
  useEffect(() => {
    if (readonly) refetch();
  }, [licenseChanged, refetch, readonly]);
  // as completeDataResponse it returns single object
  const licenseInfo: LicenseInfo = data as LicenseInfo;

  const userType = value.userKind === UserClass.BENUTZER;
  const employeeType =
    value.userKind === UserClass.MITARBEITER && !value.isLightUser;
  const lightUserType =
    value.userKind === UserClass.MITARBEITER && value.isLightUser;

  const userLicenses =
    value.assignedLicenses
      ?.filter((x) => {
        if (userType) {
          return FULL_USER_VISIBLE_MODULES.includes(x.license);
        }
        if (lightUserType) {
          return NON_FULL_USER_VISIBLE_MODULES.includes(x.license);
        }
        return false;
      })
      // keep order of modules
      .sort((a, b) => {
        if (userType) {
          return (
            FULL_USER_VISIBLE_MODULES.indexOf(a.license) -
            FULL_USER_VISIBLE_MODULES.indexOf(b.license)
          );
        }
        if (lightUserType) {
          return (
            NON_FULL_USER_VISIBLE_MODULES.indexOf(a.license) -
            NON_FULL_USER_VISIBLE_MODULES.indexOf(b.license)
          );
        }
        return 0;
      }) || [];
  const specialModules =
    value.assignedLicenses?.filter((x) => {
      if (userType) {
        return FULL_USER_VISIBLE_SPECIAL_MODULES.includes(x.license);
      }
      if (lightUserType) {
        return NON_FULL_USER_VISIBLE_SPECIAL_MODULES.includes(x.license);
      }
      return false;
    }) || [];

  const userName = () => {
    if (userType) return t('USER.USER.FULL');
    if (employeeType) return t('USER.LICENSE.EMPLOYEE');

    return t('USER.LICENSE.LIGHT');
  };

  const currentUsers =
    useUserCounts() + (value.fulllUsersInUse ? value.fulllUsersInUse : 0);

  const maxUsers = licenseInfo.modules?.find(
    (x) => x.module === 'CRM_MASKEN'
  )?.moduleCount;

  return (
    <>
      <Collapse defaultOpen title={t('USER.USER_KINDS')}>
        {readonly ? (
          <Typography>{userName()}</Typography>
        ) : (
          <>
            <Box display="flex" gap="1rem" alignItems="center">
              <CheckboxRadioItem
                control="radio"
                label={t(`USER.USER.FULL`) + ` (${currentUsers} / ${maxUsers})`}
                defaultChecked={userType}
                disabled={currentUsers >= maxUsers}
                onChange={() => {
                  onUserChange({
                    userKind: UserClass.BENUTZER,
                    isLightUser: false,
                    fulllUsersInUse: value.fulllUsersInUse ? 0 : 1,
                  });
                }}
              />
              <Tooltip title={t('USER.FULL_INFO')}>
                <InfoIcon color="primary" fontSize="medium" />
              </Tooltip>
            </Box>
            <Box paddingLeft="2rem">
              <CheckboxRadioItem
                control="checkbox"
                label={t(`USER.ADMINISTRATOR`)}
                disabled={value.userKind !== UserClass.BENUTZER}
                defaultChecked={value.isMaster === -1}
                onChange={(__event, value) => {
                  onUserChange({
                    isMaster: value ? -1 : 0,
                  });
                }}
              />
            </Box>
            <CheckboxRadioItem
              control="radio"
              label={t(`USER.LICENSE.LIGHT`)}
              defaultChecked={lightUserType}
              onChange={() => {
                // light user?
                onUserChange({
                  isMaster: 0,
                  userKind: UserClass.MITARBEITER,
                  isLightUser: true,
                  fulllUsersInUse: value.wasFullUser ? -1 : 0,
                });
              }}
            />
            <CheckboxRadioItem
              control="radio"
              label={t(`USER.LICENSE.EMPLOYEE`)}
              defaultChecked={employeeType}
              onChange={() => {
                onUserChange({
                  userKind: UserClass.MITARBEITER,
                  isMaster: 0,
                  isLightUser: false,
                  fulllUsersInUse: value.wasFullUser ? -1 : 0,
                });
              }}
            />

            <Divider />
            <CheckboxRadioItem
              control="radio"
              label={t(`USER.LICENSE.API_USER`)}
              defaultChecked={false}
              disabled
              onChange={() => {
                // noop - It will come but we want to have placeholder
              }}
            />
          </>
        )}
      </Collapse>
      {(userType || lightUserType) && (
        <>
          <Collapse
            defaultOpen
            title={t('USER.ASSIGNED_LICENSES')}
            headerBar={
              error && (
                <Box display="flex" padding="1rem">
                  <Typography
                    color="error"
                    style={{
                      backgroundColor: 'var(--bgShade4)',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 1rem',
                    }}
                  >
                    {error}
                  </Typography>
                </Box>
              )
            }
          >
            {readonly && userLicenses.filter((x) => x.assigned).length === 0 ? (
              <Typography>{t('FILE_CONTACTS.OPEN_POSITIONS.NONE')}</Typography>
            ) : (
              userLicenses.map((userLicense) => (
                <LicenseCheckbox
                  licenseInfo={licenseInfo}
                  onChange={(lic, isInGroup) => {
                    // If we edit parent of the group, children will be unselected so we need to decrease number of license
                    if (isInGroup) {
                      const localLicense = userLicenses.find(
                        (x) => x.license === lic.license
                      );
                      lic.inUse = localLicense.assigned ? -1 : 0;
                      onChange(lic);
                    }
                    onChange(lic);
                  }}
                  userLicense={userLicense}
                  allLicenses={userLicenses}
                  readonly={readonly}
                />
              ))
            )}
          </Collapse>
          <Collapse defaultOpen title={t('USER.ASSIGNED_SPECIAL_MODULES')}>
            {readonly &&
            specialModules.filter((x) => x.assigned).length === 0 ? (
              <Typography>{t('FILE_CONTACTS.OPEN_POSITIONS.NONE')}</Typography>
            ) : (
              specialModules.map((userLicense) => (
                <LicenseCheckbox
                  licenseInfo={licenseInfo}
                  onChange={onChange}
                  userLicense={userLicense}
                  readonly={readonly}
                  allLicenses={userLicenses}
                />
              ))
            )}
          </Collapse>
        </>
      )}
    </>
  );
};

interface LicenseCheckboxProps {
  licenseInfo: LicenseInfo;
  allLicenses: LocalUserAssignedLicense[];
  userLicense: LocalUserAssignedLicense;
  onChange: (lic: LocalUserAssignedLicense, isInGroup: boolean) => void;
  readonly?: boolean;
}

const useLicenseDetails = (
  licenseInfo: LicenseInfo,
  userLicense: LocalUserAssignedLicense,
  readonly?: boolean
) => {
  const module = licenseInfo?.modules?.find(
    (y) => userLicense?.license === y.userLicense
  );

  const [inUse, setInUse] = useState(module?.moduleCountInUse);
  useEffect(() => {
    setInUse((x) => {
      if (x === undefined || readonly) return module?.moduleCountInUse;
      return x;
    });
  }, [module?.moduleCountInUse]);
  const max = module?.moduleCount;

  if (!userLicense) return null;

  const currentInUse =
    inUse + ('inUse' in userLicense ? (userLicense.inUse as number) : 0);

  return {
    max,
    currentInUse,
    module,
  };
};

const LicenseCheckbox = ({
  licenseInfo,
  userLicense,
  onChange,
  readonly,
  allLicenses,
}: LicenseCheckboxProps) => {
  const { t } = useTranslation();

  const {
    max,
    currentInUse,
    module: currentModule,
  } = useLicenseDetails(licenseInfo, userLicense, readonly);

  const group = LICENSE_GROUPS.find((group) =>
    group.children.includes(userLicense.license)
  );

  const parentLicense = allLicenses?.find((x) => x.license === group?.parent);

  const parentLicenseDetails = useLicenseDetails(
    licenseInfo,
    parentLicense,
    readonly
  );

  const usageInformation = `(${currentInUse} / ${max})`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        paddingLeft: group ? '2rem' : '0',
      }}
    >
      {readonly ? (
        userLicense.assigned ? (
          <Box display="flex" alignItems="center" gap="1rem" paddingY="0.5rem">
            <CheckCircleOutlineIcon color="success" />
            <Typography>{t(`USER.LICENSE.${userLicense.license}`)}</Typography>
          </Box>
        ) : (
          <></>
        )
      ) : (
        <>
          <CheckboxRadioItem
            control="checkbox"
            label={t(`USER.LICENSE.${userLicense.license}`)}
            defaultChecked={userLicense.assigned}
            disabled={
              (currentModule && currentInUse >= max && !userLicense.assigned) ||
              (parentLicenseDetails &&
                parentLicenseDetails.module &&
                parentLicenseDetails.currentInUse >= parentLicenseDetails.max &&
                !parentLicense.assigned)
            }
            onChange={(__event, value) => {
              const onChangeValue: LocalUserAssignedLicense = {
                license: userLicense.license,
                assigned: value,
              };
              if (!readonly) {
                onChangeValue.inUse = userLicense.inUse ? 0 : value ? 1 : -1;
              }
              onChange(onChangeValue, false);

              const children = LICENSE_GROUPS.find(
                (group) => group.parent === userLicense.license
              )?.children;
              if (children && !value) {
                children.forEach((child) => {
                  onChange(
                    {
                      license: child,
                      assigned: false,
                    },
                    true
                  );
                });
              }

              if (parentLicense && !parentLicense.assigned) {
                onChange(
                  {
                    license: parentLicense.license,
                    assigned: true,
                    inUse: parentLicense.inUse ? 0 : 1,
                  },
                  false
                );
              }
            }}
          />
          {currentModule && (
            <Typography color="var(--text03)">{usageInformation}</Typography>
          )}
        </>
      )}
    </div>
  );
};

const useUserCounts = () => {
  const request: DataRequest = useMemo(() => {
    return {
      entity: Entities.fullUser,
      data: {},
      filter: [
        {
          userKind: { $eq: UserClass.BENUTZER },
        },
        {
          hasLeft: { $eq: false },
        },
      ],
    };
  }, []);

  const response = useDataProvider<FullUser>(request);
  return response.total;
};
